// Figma: `Dropdown Component - Item List` fills its trigger, 10px below — 444 (68:2789),
// 200 (2343:2201), 414 (1437:2986). `Dropdown Component - Options List` (213:4320) is a
// fixed 280px, 14px below. Figma declares no trigger width and no floor; 160 and 400 are ours.

export const MENU_VIEWPORT_PADDING_PX = 8;

export const MENU_MIN_WIDTH_PX = 160;

// Figma draws the Options menu at a flat 280. Taken as a ceiling rather than a width: a kebab
// of four short verbs then carries 130px of air, and every kebab in a consuming app is the same
// width whatever it holds. 180 is the floor those menus sized themselves to before this preset.
export const MENU_OPTIONS_MIN_WIDTH_PX = 180;

export type MenuPreset = 'standard' | 'options';

export interface MenuSpec {
    /** `'trigger'` fills the control that opened it; `'content'` sizes to the widest item. */
    width: 'trigger' | 'content';
    /** Where this preset stops shrinking. */
    minWidth: number;
    /** Where a content-sized one stops growing. A caller's `minWidth` still raises past it. */
    maxWidth?: number;
    align: 'left' | 'right';
    gap: number;
    radius: number;
    padding: number;
    shadow: string;
    background: string;
    border: string;
}

export const MENU_SPECS: Record<MenuPreset, MenuSpec> = {
    standard: {
        width: 'trigger',
        minWidth: MENU_MIN_WIDTH_PX,
        align: 'left',
        gap: 10,
        radius: 12,
        padding: 4,
        shadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.1)',
        background: 'var(--malachite-card-background, rgb(255, 255, 255))',
        border: '1px solid var(--malachite-card-border, rgb(232, 232, 232))',
    },
    options: {
        width: 'content',
        minWidth: MENU_OPTIONS_MIN_WIDTH_PX,
        maxWidth: 280,
        align: 'right',
        gap: 14,
        radius: 10,
        padding: 8,
        shadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.1)',
        background: 'var(--malachite-card-background, rgb(255, 255, 255))',
        border: '1px solid var(--malachite-card-border, rgb(232, 232, 232))',
    },
};

export const MENU_MAX_HEIGHT_PX = 400;

export interface MenuGeometry {
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    openUp: boolean;
}

export interface MenuGeometryInput {
    triggerRect: Pick<DOMRect, 'top' | 'left' | 'right' | 'bottom' | 'width'>;
    preset: MenuPreset;
    minWidth?: number;
    /** The widest item, for a content-sized preset. Absent until the menu has been laid out. */
    contentWidth?: number;
    menuHeight: number;
    viewportWidth: number;
    viewportHeight: number;
}

export const computeMenuGeometry = ({
    triggerRect,
    preset,
    minWidth,
    contentWidth,
    menuHeight,
    viewportWidth,
    viewportHeight,
}: MenuGeometryInput): MenuGeometry => {
    const spec = MENU_SPECS[preset];
    const available = viewportWidth - MENU_VIEWPORT_PADDING_PX * 2;

    const floor = Math.max(spec.minWidth, minWidth ?? 0);
    const ceiling = Math.min(available, Math.max(spec.maxWidth ?? available, floor));
    const natural = spec.width === 'trigger' ? triggerRect.width : (contentWidth ?? ceiling);
    const width = Math.max(0, Math.min(Math.max(natural, floor), ceiling));

    const preferredLeft = spec.align === 'right' ? triggerRect.right - width : triggerRect.left;
    const oppositeLeft = spec.align === 'right' ? triggerRect.left : triggerRect.right - width;
    const minLeft = MENU_VIEWPORT_PADDING_PX;
    const maxLeft = viewportWidth - width - MENU_VIEWPORT_PADDING_PX;
    const fits = (candidate: number) => candidate >= minLeft && candidate <= maxLeft;

    let left: number;
    if (fits(preferredLeft)) {
        left = preferredLeft;
    } else if (fits(oppositeLeft)) {
        left = oppositeLeft;
    } else {
        left = Math.max(minLeft, Math.min(preferredLeft, maxLeft));
    }

    // Which side, then how much of it. "More room above" is not "enough room above", so the
    // height is clamped to whichever side won. Clamping `top` instead keeps the menu on screen
    // by painting it over its own trigger, which also swallows the click that closes it.
    const roomBelow = viewportHeight - triggerRect.bottom - spec.gap - MENU_VIEWPORT_PADDING_PX;
    const roomAbove = triggerRect.top - spec.gap - MENU_VIEWPORT_PADDING_PX;
    const openUp = menuHeight > 0 && menuHeight > roomBelow && roomAbove > roomBelow;

    const maxHeight = Math.max(0, Math.min(MENU_MAX_HEIGHT_PX, openUp ? roomAbove : roomBelow));
    const height = menuHeight > 0 ? Math.min(menuHeight, maxHeight) : 0;

    const top = Math.max(
        MENU_VIEWPORT_PADDING_PX,
        openUp ? triggerRect.top - spec.gap - height : triggerRect.bottom + spec.gap,
    );

    return { top, left, width, maxHeight, openUp };
};

// The height decides whether the menu opens upwards, and it is only meaningful at the width the
// menu will actually have — so on the pass that first sizes the panel the width is written
// before the height is read. The guard is what keeps that out of the steady state: every later
// call finds the width already set and writes nothing, so a `ResizeObserver` callback does not
// resize what it is observing (Chrome's "undelivered notifications" error) and a per-frame
// reposition does not force a reflow. After the first pass this is a pure read.
// A content-sized preset needs the width the panel chose for itself, which is only readable
// while no width has been written to it — i.e. on the pass that opens the menu. The caller keeps
// it from there, because after the first write `offsetWidth` reports what we wrote, not what the
// items want.
export const measureContentWidth = (panel: HTMLElement | null): number | undefined => {
    if (!panel || panel.style.width !== '') {
        return undefined;
    }

    // The rows, not the panel. Each label carries `min-width: 0` and `overflow: hidden` so it
    // can ellipsise, which also means it contributes nothing to the panel's intrinsic width —
    // measuring the panel itself returns a box the size of its own padding. `scrollWidth` on a
    // clipped row is the width the row wanted.
    const rows = panel.querySelectorAll<HTMLElement>('button, [role="menuitem"]');
    let widest = 0;
    rows.forEach((row) => {
        widest = Math.max(widest, row.scrollWidth);
    });

    if (widest === 0) {
        return undefined;
    }

    // The panel is border-box, so the width we hand back has to carry its own padding and rule.
    // Each read is defaulted: a computed style can come back empty — jsdom never expands the
    // `border` shorthand — and one NaN here would make the whole width NaN, which CSS drops,
    // leaving the panel unsized with no sign of why.
    const styles = getComputedStyle(panel);
    const edge = (value: string) => Number.parseFloat(value) || 0;
    const chrome =
        edge(styles.paddingLeft) +
        edge(styles.paddingRight) +
        edge(styles.borderLeftWidth) +
        edge(styles.borderRightWidth);

    return Math.ceil(widest + chrome);
};

export const measureMenuGeometry = (
    panel: HTMLElement | null,
    input: Omit<MenuGeometryInput, 'menuHeight'>,
): MenuGeometry => {
    if (!panel) {
        return computeMenuGeometry({ ...input, menuHeight: 0 });
    }

    const cssWidth = `${computeMenuGeometry({ ...input, menuHeight: 0 }).width}px`;
    if (panel.style.width !== cssWidth) {
        panel.style.width = cssWidth;
    }

    return computeMenuGeometry({ ...input, menuHeight: panel.offsetHeight });
};

export const isTriggerOffscreen = (
    rect: Pick<DOMRect, 'top' | 'left' | 'right' | 'bottom'>,
    viewportWidth: number,
    viewportHeight: number,
): boolean =>
    rect.bottom < 0 || rect.top > viewportHeight || rect.right < 0 || rect.left > viewportWidth;

export const rectSignature = (rect: Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>): string =>
    `${rect.top},${rect.left},${rect.width},${rect.height}`;
